import React from "react";
import { PriorityChip } from "../../crmpostventa-shared/components/Chips";
import { rowKey, motivoLabel } from "../ipcFormat";
import {
  Actor,
  AntiguedadCell,
  CanalCell,
  ComercioCell,
  DuracionCell,
  EstadoCell,
  FechaCierreCell,
  HistoricoChip,
  IpcStatusChip,
  ManualMark,
  MotivoCell,
  Muted,
  OrigenChip,
  PrioridadCell,
  ProximoContactoCell,
  ResponsableCell,
  ResultadoCell,
  TipoChip,
  motivoCierreLabel,
} from "./ipcCells";

const ACTIVAS_HEADERS = ["Comercio", "Tipo", "Motivo", "Canal", "Estado", "Prioridad", "Próximo contacto", "Responsable", "Antigüedad"];
const FINALIZADAS_HEADERS = ["Comercio", "Tipo", "Motivo", "Resultado", "Fecha de cierre", "Duración", "Responsable"];

// Headers of a table before the action column: Tipo only in the Todas section.
const headersFor = (finalizadas, showTipo) =>
  (finalizadas ? FINALIZADAS_HEADERS : ACTIVAS_HEADERS).filter((header) => showTipo || header !== "Tipo");

// Columns with the action column, so the loading skeleton has the same shape as the table.
export const columnsFor = (finalizadas, showTipo) => headersFor(finalizadas, showTipo).length + 1;

const isSpotlight = (item, spotlight) => Boolean(spotlight) && !item.esHistorico && item.id === spotlight.id;
const newBadge = (item, spotlight) => (isSpotlight(item, spotlight) && spotlight.isNew ? "Nueva" : null);

const rowClass = (item, busyId, spotlight) =>
  [
    busyId === rowKey(item) ? "is-busy" : "",
    isSpotlight(item, spotlight) ? "is-spotlight" : "",
    isSpotlight(item, spotlight) && spotlight.isNew ? "is-new" : "",
  ]
    .filter(Boolean)
    .join(" ") || undefined;

// DataTable of Activas or Finalizadas (design Prompt 1). The card scrolls sideways when the columns do not fit.
export function IpcTable({ items, finalizadas, showTipo, currentUserId, today, busyId, spotlight, renderActions }) {
  return (
    <div className="crm-rollo__table-wrap">
      <table className="crm-rollo__table">
        <thead>
          <tr>
            {headersFor(finalizadas, showTipo).map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
            <th scope="col">
              <span className="crm-rollo__sr-only">Acciones</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={rowKey(item)} className={rowClass(item, busyId, spotlight)} aria-busy={busyId === rowKey(item) || undefined}>
              <td className="crm-rollo__cell--comercio">
                <ComercioCell item={item} badge={newBadge(item, spotlight)} />
              </td>
              {showTipo ? (
                <td className="crm-rollo__nowrap">
                  <TipoChip item={item} />
                </td>
              ) : null}
              <td className="crm-ipc__cell--motivo">
                <MotivoCell item={item} />
              </td>
              {finalizadas ? (
                <>
                  <td className="crm-ipc__cell--resultado">
                    <ResultadoCell item={item} />
                  </td>
                  <td className="crm-rollo__nowrap">
                    <FechaCierreCell item={item} />
                  </td>
                  <td className="crm-rollo__nowrap">
                    <DuracionCell item={item} />
                  </td>
                </>
              ) : (
                <>
                  <td className="crm-rollo__nowrap">
                    <CanalCell item={item} />
                  </td>
                  <td className="crm-rollo__cell--estado">
                    <EstadoCell item={item} />
                  </td>
                  <td>
                    <PrioridadCell item={item} />
                  </td>
                  <td className="crm-rollo__nowrap">
                    <ProximoContactoCell item={item} today={today} />
                  </td>
                </>
              )}
              <td className="crm-rollo__nowrap">
                <ResponsableCell item={item} currentUserId={currentUserId} />
              </td>
              {finalizadas ? null : (
                <td className="crm-rollo__nowrap">
                  <AntiguedadCell item={item} />
                </td>
              )}
              <td>{renderActions(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Meta = ({ label, children }) => (
  <div>
    <dt className="crm-rollo__lbl">{label}</dt>
    <dd>{children}</dd>
  </div>
);

// MobileCard of each row: priority (Activas) or result (Finalizadas) in the head, then type (only in Todas),
// motive, origin and the data of the view.
export function IpcCards({ items, finalizadas, showTipo, currentUserId, today, busyId, spotlight, renderActions }) {
  return (
    <div className="crm-rollo__cards">
      {items.map((item) => {
        const className = ["crm-rollo__card", rowClass(item, busyId, spotlight)].filter(Boolean).join(" ");
        return (
          <article key={rowKey(item)} className={className} aria-busy={busyId === rowKey(item) || undefined}>
            <div className="crm-rollo__card-head">
              <div>
                <ComercioCell item={item} badge={newBadge(item, spotlight)} />
              </div>
              {finalizadas ? <IpcStatusChip item={item} /> : item.prioridad ? <PriorityChip prioridad={item.prioridad} /> : null}
            </div>
            <div className="crm-rollo__s-row crm-ipc__tags">
              {showTipo ? <TipoChip item={item} /> : null}
              <span>{motivoLabel(item)}</span>
              <OrigenChip origen={item.origen} />
              {item.esHistorico ? <HistoricoChip /> : null}
            </div>
            {finalizadas ? (
              <dl className="crm-rollo__card-meta">
                <Meta label="Motivo de cierre">{motivoCierreLabel(item) || <Muted />}</Meta>
                <Meta label="Cierre">
                  <FechaCierreCell item={item} />
                </Meta>
                <Meta label="Duración">
                  <DuracionCell item={item} />
                </Meta>
                <Meta label="Responsable">
                  <ResponsableCell item={item} currentUserId={currentUserId} />
                </Meta>
              </dl>
            ) : (
              <>
                <div className="crm-rollo__s-row">
                  <IpcStatusChip item={item} />
                  <Actor estado={item.estado} />
                </div>
                {item.prioridadManual ? <ManualMark long /> : null}
                <dl className="crm-rollo__card-meta">
                  <Meta label="Próximo contacto">
                    <ProximoContactoCell item={item} today={today} />
                  </Meta>
                  <Meta label="Canal">
                    <CanalCell item={item} />
                  </Meta>
                  <Meta label="Responsable">
                    <ResponsableCell item={item} currentUserId={currentUserId} />
                  </Meta>
                  <Meta label="Antigüedad">
                    <AntiguedadCell item={item} />
                  </Meta>
                </dl>
              </>
            )}
            {renderActions(item, true)}
          </article>
        );
      })}
    </div>
  );
}
